import { DashboardLineChart } from "@aidkitorg/types/lib/admin-panel/dashboard";
import React, { useEffect, useState } from "react";
import { generateDistroSummaryKind } from "../../../utils/adminPanel.utils";
import { ReportFilterConfig, FilterGroupToBooleanExprConverter, BooleanExprToFilterGroupConverter } from "@aidkitorg/types/lib/admin-panel/filters";
import { BooleanExpr } from "@aidkitorg/types/lib/survey";

export type XAxis = {
  title: string,
  dataField: string,
  bucket: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year',
}

export type YAxis = {
  title: string,
  aggregation: string,
  dataField: string,
}

export const useLineChartFormState = (component?: DashboardLineChart) => {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [filterConfig, setFilterConfig] = useState<ReportFilterConfig | undefined>(undefined)
  const [showPercentages, setShowPercentages] = useState(false)
  const [titleHasError, setTitleHasError] = useState(false)

  const [xAxis, setXAxis] = useState<XAxis>({
    title: '',
    dataField: '',
    bucket: 'day'
  })
  const [yAxis, setYAxis] = useState<YAxis>({
    title: '',
    aggregation: 'Count',
    dataField: '',
  })

  useEffect(() => {
    if (!component) return

    const componentTitle = 'title' in component && component.title
      ? typeof component.title === 'string'
        ? component.title
        : component.title['en']
      : 'Dashboard Component';
    setTitle(componentTitle)
    setDescription(typeof component.description === 'object' ? component.description?.content?.en || '' : component.description || '')

    // Load filter config if present
    if (component.filter) {
      try {
        const filterGroup = BooleanExprToFilterGroupConverter.convert(component.filter);
        if (filterGroup) {
          setFilterConfig({
            enabled: true,
            config: filterGroup
          });
        }
      } catch (error) {
        console.warn('Failed to convert line chart filter to FilterConfig:', error);
      }
    }

    const groups = component.groups
    if (groups.length === 1 && groups[0].kind === 'Bucket Date') {
      setXAxis({
        title: component.xAxisLabel || '',
        dataField: groups[0].value.kind === 'As Date' ?
          (
            groups[0].value.value.kind === 'Field' ? groups[0].value.value.field : ''
          ) : '',
        bucket: groups[0].bucket
      })
    }

    const yAxisSummary: YAxis = {
      title: component.yAxisLabel || '',
      aggregation: component.summary.kind,
      dataField: ''
    }

    if (component.summary.kind === 'Sum' || component.summary.kind === 'Mean') {
      if (component.summary.value.kind === 'Field') {
        yAxisSummary.dataField = component.summary.value.field
      }
    }
    setYAxis(yAxisSummary)

    setShowPercentages(component.showPercentages === true)
  }, [component])

  const buildLineChartObject = (): DashboardLineChart => {
    // Convert FilterConfig to BooleanExpr if filter is enabled
    let booleanExpr: BooleanExpr | undefined = undefined;
    if (filterConfig && filterConfig.enabled && filterConfig.config) {
      try {
        booleanExpr = FilterGroupToBooleanExprConverter.convert(filterConfig.config);
      } catch (error) {
        console.warn('Failed to convert filter config to boolean expression:', error);
      }
    }

    const lineChart: DashboardLineChart = {
      kind: 'Line Chart',
      title: title,
      ...(description && { description: { content: { en: description }, placement: 'top' as const } }),
      xAxisLabel: xAxis.title,
      yAxisLabel: yAxis.title,
      summary: generateDistroSummaryKind(yAxis.aggregation, yAxis.dataField),
      groups: [{
        kind: 'Bucket Date',
        bucket: xAxis.bucket,
        value: {
          kind: 'As Date',
          value: {
            kind: 'Field',
            field: xAxis.dataField
          }
        }
      }],
      showPercentages,
      ...(booleanExpr && { filter: booleanExpr })
    }

    return lineChart
  }

  const isFormStateValid =
    title !== '' && !titleHasError &&
    xAxis.dataField !== '' &&
    (yAxis.aggregation === 'Count' || yAxis.dataField !== '')

  return {
    title, setTitle,
    description, setDescription,
    xAxis, setXAxis,
    yAxis, setYAxis,
    filterConfig, setFilterConfig,
    showPercentages, setShowPercentages,
    setTitleHasError,
    buildLineChartObject,
    isFormStateValid,
  }
}